import { Component } from '@theme/component';
import { debounce, onAnimationEnd, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {{ keydown: (event: KeyboardEvent) => void; focusin: (event: FocusEvent) => void } | null}
   */
  #focusTrapHandlers = null;

  /**
   * Syncs trap with `<dialog open>` — more reliable than `dialog:open` alone (refs / timing / morph).
   * @type {MutationObserver | null}
   */
  #modalOpenObserver = null;

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /**
   * Native modal host for the search overlay (predictive search lives inside `<dialog>`).
   * @returns {HTMLDialogElement | null}
   */
  #getModalHost() {
    const host = this.closest('dialog');
    return host instanceof HTMLDialogElement ? host : null;
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    const modalHost = this.#getModalHost();
    if (modalHost) {
      this.#modalOpenObserver = new MutationObserver(() => this.#syncModalFocusTrap());
      this.#modalOpenObserver.observe(modalHost, { attributes: true, attributeFilter: ['open'] });
      this.#syncModalFocusTrap();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpenFocusTrap, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    onDocumentLoaded(() => {
      this.resetSearch(false); // Pass false to avoid focusing the input
    });
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#modalOpenObserver?.disconnect();
    this.#modalOpenObserver = null;
    this.#removeFocusTrap();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#removeFocusTrap();
    this.#resetSearch();
  };

  /**
   * Module script can load after `showModal()` so `open` is already set before
   * `MutationObserver` runs — `dialog:open` plus rAF catches that first paint.
   */
  #onDialogOpenFocusTrap = () => {
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#syncModalFocusTrap();
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#syncModalFocusTrap();
      });
    });
  };

  #syncModalFocusTrap = () => {
    const host = this.#getModalHost();
    if (host?.open) {
      this.#installFocusTrap();
    } else {
      this.#removeFocusTrap();
    }
  };

  /**
   * Same selector family as `trapFocus` in `focus.js`, plus open shadow roots (e.g. slideshow controls).
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  #getFocusableElements(container) {
    const selector = [
      'summary',
      'a[href]',
      'button:not([disabled])',
      "[tabindex]:not([tabindex^='-'])",
      '[draggable]',
      'area',
      'input:not([type=hidden]):not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'object',
      'iframe',
    ].join(', ');

    /** @type {HTMLElement[]} */
    const list = [];

    /**
     * @param {Element | ShadowRoot} scope
     */
    const collectFromScope = (scope) => {
      for (const el of scope.querySelectorAll(selector)) {
        if (!(el instanceof HTMLElement)) continue;
        if (!this.#isUsableFocusTarget(el)) continue;
        list.push(el);
      }

      for (const el of scope.querySelectorAll('*')) {
        if (!(el instanceof HTMLElement)) continue;
        if (el.shadowRoot) {
          collectFromScope(el.shadowRoot);
        }
      }
    };

    collectFromScope(container);
    return list;
  }

  /**
   * @param {HTMLElement} el
   */
  #isUsableFocusTarget(el) {
    if (el.disabled) return false;
    if (el.getAttribute('aria-disabled') === 'true') return false;
    if (el.hasAttribute('hidden')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return true;
  }

  #installFocusTrap = () => {
    if (this.#focusTrapHandlers) return;
    if (!this.#getModalHost()?.open) return;

    const container = this;

    /** @param {KeyboardEvent} event */
    const keydownHandler = (event) => {
      if (event.key !== 'Tab') return;
      if (!this.#getModalHost()?.open) return;

      const focusable = this.#getFocusableElements(container);
      if (focusable.length === 0) return;

      const active = document.activeElement;
      const inside = active instanceof Node && container.contains(active);

      if (!inside) {
        event.preventDefault();
        (event.shiftKey ? focusable[focusable.length - 1] : focusable[0])?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (active === first || active === container) {
          event.preventDefault();
          last?.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    /** @param {FocusEvent} event */
    const focusinHandler = (event) => {
      if (!this.#getModalHost()?.open) return;
      if (!(event.target instanceof Node) || container.contains(event.target)) return;
      event.stopPropagation();
      const focusable = this.#getFocusableElements(container);
      focusable[0]?.focus();
    };

    document.addEventListener('keydown', keydownHandler, true);
    document.addEventListener('focusin', focusinHandler, true);
    this.#focusTrapHandlers = { keydown: keydownHandler, focusin: focusinHandler };
  };

  #removeFocusTrap = () => {
    if (!this.#focusTrapHandlers) return;
    document.removeEventListener('keydown', this.#focusTrapHandlers.keydown, true);
    document.removeEventListener('focusin', this.#focusTrapHandlers.focusin, true);
    this.#focusTrapHandlers = null;
  };

  /**
   * Close the search dialog while ensuring keyboard focus returns to the
   * search trigger button instead of falling back to the document body.
   *
   * The trigger is focused immediately and the dialog is closed 300ms later;
   * setting focus before the dialog closes prevents the browser from losing
   * focus during the close animation.
   */
  closeAndFocusTrigger = () => {
    const { dialog } = this;
    if (!dialog) return;

    this.#removeFocusTrap();

    const trigger = /** @type {HTMLElement | null} */ (
      document.querySelector('search-button button:not([hidden])') ??
        document.querySelector('search-button button') ??
        document.querySelector('search-button a:not([hidden])') ??
        document.querySelector('search-button a')
    );

    if (trigger) {
      trigger.focus();
    }

    setTimeout(() => {
      dialog.closeDialog();
    }, 300);
  };

  /**
   * Handles keydown events on the search form and input.
   * - Escape: closes/resets the predictive search.
   * - Enter on the input: if there is a single result, redirect straight to it;
   *   otherwise fall through and let the form submit naturally to the search page.
   *
   * Tab/Shift+Tab in the search modal are handled by `#installFocusTrap` (document capture).
   * Arrow keys are not intercepted here.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.key === 'Enter' && event.target === this.refs.searchInput) {
      const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
      if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
        event.preventDefault();
        window.location.href = singleResultContainer.dataset.singleResultUrl;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#syncModalFocusTrap();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    try {
      const { predictiveSearchResults, searchInput } = this.refs;
      const emptySectionId = 'predictive-search-empty';

      searchInput.value = '';
      this.#hideResetButton();

      const abortController = this.#createAbortController();
      const url = new URL(window.location.href);
      url.searchParams.delete('page');

      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

      /** This needs to be awaited and not .then so the DOM is already morphed
       * when #closeResults is called and therefore the height is animated */
      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (!recentlyViewedMarkup) return;

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
        if (!recentlyViewedProductsHtml) return;

        for (const child of recentlyViewedProductsHtml.children) {
          if (child instanceof HTMLElement) {
            child.setAttribute('ref', 'recentlyViewedWrapper');
          }
        }

        const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
        if (!collectionElement) return;
        collectionElement.prepend(...recentlyViewedProductsHtml.children);
      }

      if (abortController.signal.aborted) return;

      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      this.#resetScrollPositions();
    } finally {
      this.#syncModalFocusTrap();
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
